import { SEO_CONFIG } from '@/constants/seo';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: any;
  noIndex?: boolean;
  canonical?: string;
}

export function SEO({
  title = SEO_CONFIG.DEFAULT_TITLE,
  description = SEO_CONFIG.DEFAULT_DESCRIPTION,
  keywords = [],
  ogTitle,
  ogDescription,
  ogImage = SEO_CONFIG.OG_IMAGE,
  ogUrl,
  ogType = SEO_CONFIG.OG_TYPE,
  twitterTitle,
  twitterDescription,
  twitterImage,
  structuredData,
  noIndex = false,
  canonical,
}: SEOProps) {
  // Open Graph 기본값 설정
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;
  const finalOgUrl = ogUrl || SEO_CONFIG.SITE_URL;
  
  // Twitter Card 기본값 설정
  const finalTwitterTitle = twitterTitle || finalOgTitle;
  const finalTwitterDescription = twitterDescription || finalOgDescription;
  const finalTwitterImage = twitterImage || ogImage;
  
  // 키워드 문자열 생성
  const keywordsString = keywords.length > 0 ? keywords.join(', ') : '';

  return (
    <>
      {/* 기본 메타태그 */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywordsString && <meta name="keywords" content={keywordsString} />}
      
      {/* 추가 메타태그 */}
      <meta name="author" content="에코딜" />
      <meta name="publisher" content="에코딜" />
      <meta name="application-name" content="에코딜" />
      <meta name="apple-mobile-web-app-title" content="에코딜" />
      
      {/* 검색 엔진 제어 */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Open Graph 메타태그 */}
      <meta property="og:site_name" content={SEO_CONFIG.SITE_NAME} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={finalOgUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="ko_KR" />
      
      {/* Twitter Card 메타태그 */}
      <meta name="twitter:card" content={SEO_CONFIG.TWITTER_CARD_TYPE} />
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={finalTwitterImage} />
      
      {/* 구조화 데이터 */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* 언어별 대응 (향후 확장) */}
      <link rel="alternate" hrefLang="ko" href="https://ecodeal.kr" />
      <link rel="alternate" hrefLang="x-default" href="https://ecodeal.kr" />
    </>
  );
}

// 편의를 위한 사전 정의된 SEO 컴포넌트들
interface HomeSEOProps {
  structuredData?: any[];
}

export function HomeSEO({ structuredData }: HomeSEOProps) {
  return (
    <SEO
      title="에코딜(EcoDeal) - 마감할인 40-50% | 음식물 쓰레기 줄이기 | 못난이 식품 할인"
      description="마감 임박 음식 40-50% 할인! 김밥, 도시락, 샐러드, 베이커리 마감세일. 1인가구 혼밥 할인, 남은 음식 판매, 재고 떨이, 유통기한 임박 상품. 서울, 경기, 인천 지역 음식점 마감할인 플랫폼"
      keywords={[
        '에코딜', 'ecodeal', '에코 딜', 'eco deal',
        '마감할인', '마감세일', '땡처리', '떨이', '재고처분',
        '예약할인', '예약구매', '사전예약 할인',
        '40프로 할인', '50프로 할인', '반값 할인',
        '김밥 할인', '도시락 할인', '샐러드 할인', '베이커리 할인',
        '카페 마감할인', '분식 마감할인', '한식 마감할인',
        '1인가구 할인', '혼밥 할인', '자취생 할인',
        '직장인 점심 할인', '저녁 할인', '야식 할인',
        '음식물쓰레기줄이기', '푸드테크', '친환경소비',
        '못난이식품', '못난이농산물', '잉여식품',
        '음식낭비방지', '제로웨이스트', '지속가능한소비',
        '라스트오더 대체', '굿투고 대체', '어글리어스 대체',
        '서울 마감할인', '강남 마감할인', '홍대 마감할인',
        '경기도 마감할인', '인천 마감할인'
      ]}
      ogTitle="에코딜 - 매일 40-50% 음식 할인"
      ogDescription="마감 임박 음식을 최대 50% 할인! 지구도 지키고 돈도 절약하는 똑똑한 소비"
      twitterTitle="에코딜 - 오늘의 마감할인 40-50%"
      twitterDescription="음식물 쓰레기를 줄이며 할인받는 친환경 플랫폼"
      structuredData={structuredData}
    />
  );
}